import React, { useEffect, useState } from 'react';
import { Spinner } from 'react-bootstrap';
import BookingService from '../../../Services/BookingService';
import { formatTripWhen, routeLabel } from '../../../Utils/bookingContext';

/**
 * The booking a conversation is about.
 *
 * THIS PANEL IS MOST OF WHY THE VIEWER LIVES IN THE DASHBOARD rather than in
 * a mail client: the question an agent actually has while reading a customer's
 * mail is "is this one paid, and does it have a driver yet". A mail client
 * cannot answer it; this is two fields away from the booking record.
 *
 * IT RESOLVES OR IT IS ABSENT. The reference is read out of the correspondence
 * (see Utils/bookingReference) because the archive holds no conversation→
 * booking link, so plenty of conversations have none — a sales enquiry, a
 * supplier, a newsletter. Nothing resolving is the normal case and renders
 * nothing, never an error.
 *
 * ── THE SHAPE MAPPING ──
 * Utils/bookingContext's display helpers were written for the flat
 * `BookingContext` the invoicing API attaches to a document (`from`, `to`,
 * `date`, `passengers` as a count). A raw booking is nested
 * (`destination_from.address`, `passengers.amount`, `category.name`), so it is
 * mapped INTO that flat shape and the existing helpers are reused rather than
 * a second set of route/date formatters being written here.
 */
const flatten = (booking) => ({
  booking_reference: booking.booking_reference,
  customer_name: booking.full_name,
  from: booking.destination_from?.address,
  to: booking.destination_to?.address,
  date: booking.date_datetime || booking.date,
  passengers: booking.passengers?.amount,
  category: booking.category?.name,
});

/**
 * Whether a driver is on the booking.
 *
 * BOTH PLACES, because a booking carries its driver in one of two shapes: the
 * legacy single `operator_driver_id`, and the `operators[]` entries used for
 * anything with more than one supplier (an experience with a driver and a
 * guide). Reading only the legacy field reports "no driver" on every
 * multi-operator booking, which is the exact gap that has bitten gates on
 * `operator_id` elsewhere in this codebase.
 */
const driverState = (booking) => {
  const legacy = !!booking.operator_driver_id;
  const assigned = (booking.operators || []).some((entry) => entry?.driver_id);
  return legacy || assigned ? 'Driver assigned' : 'No driver yet';
};

/** `payment.paid` is the booking's own flag — not a sum over Payment rows. */
const paidState = (booking) => (booking.payment?.paid ? 'Paid' : 'Not paid');

function BookingContextPanel({ reference }) {
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let live = true;
    if (!reference) {
      setBooking(null);
      return undefined;
    }
    setLoading(true);
    BookingService.getBookingByReference(reference)
      .then((result) => {
        if (live) setBooking(result);
      })
      .catch(() => {
        if (live) setBooking(null);
      })
      .finally(() => {
        if (live) setLoading(false);
      });
    return () => { live = false; };
  }, [reference]);

  if (!reference) return null;

  if (loading) {
    return (
      <div className="cs-booking cs-booking--loading">
        <Spinner animation="border" size="sm" className="me-2" />
        Looking up
        {' '}
        {reference}
      </div>
    );
  }

  // The reference was quoted in the mail but matches no booking — worth saying
  // plainly, because a customer writing about a reference we cannot find is
  // itself the thing an agent needs to know.
  if (!booking) {
    return (
      <div className="cs-booking cs-booking--missing" data-testid="cs-booking-missing">
        <span className="cs-booking__ref">{reference}</span>
        <span className="text-muted">mentioned here, but no such booking was found</span>
      </div>
    );
  }

  const flat = flatten(booking);

  return (
    <div className="cs-booking" data-testid="cs-booking">
      <div className="cs-booking__head">
        <span className="cs-booking__ref">{flat.booking_reference}</span>
        {flat.customer_name && <span>{flat.customer_name}</span>}
      </div>
      <dl className="cs-booking__facts">
        <div>
          <dt>Route</dt>
          <dd>{routeLabel(flat, 44) || '—'}</dd>
        </div>
        <div>
          <dt>When</dt>
          <dd>{formatTripWhen(flat.date) || '—'}</dd>
        </div>
        <div>
          <dt>Passengers</dt>
          <dd>{flat.passengers ? `${flat.passengers} pax` : '—'}</dd>
        </div>
        <div>
          <dt>Payment</dt>
          <dd>{paidState(booking)}</dd>
        </div>
        <div>
          <dt>Driver</dt>
          <dd>{driverState(booking)}</dd>
        </div>
      </dl>
    </div>
  );
}

export default BookingContextPanel;
